import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import driverImg from "../assets/driverImg.png";
import api from "../api";
import CustomTextInput from "../components/CustomTextInput";
import Layout from "../components/Layout";
import ConfirmDeletePopup from "../components/ConfirmDeletePopup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";
import CustomPhoneInput from "../components/CustomPhoneInput";

export default function DriverDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [driver, setDriver] = useState(null);
  const [name, setName] = useState("");
  const [govId, setGovId] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [error, setError] = useState(""); // Estado para errores
  const [fieldErrors, setFieldErrors] = useState({});

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    async function fetchDriver() {
      try {
        const response = await api.drivers.getOne(id);
        setDriver(response);
        setGovId(response.govId);
        setEmail(response.email);
        setName(response.name);
        setContact(response.phone);
        setError("");
      } catch (error) {
        console.error("Error fetching driver details:", error);
        setError("Error al obtener los detalles del conductor.");
      }
    }
    fetchDriver();
  }, [id]);

  const handleDeleteClick = () => {
    setIsPopupOpen(true); // Abre el popup
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.drivers.delete(id);
      console.log("Conductor eliminado");
      navigate(`/drivers`);
      setError("");
    } catch (error) {
      console.error("Error deleting driver:", error);
      setError("Error al eliminar el conductor.");
    }
    setIsPopupOpen(false); // Cierra el popup después de la confirmación
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false); // Cierra el popup sin eliminar
  };

  const handleSave = async () => {
    setFieldErrors({});
    try {
      const formData = new FormData();
      formData.append("govId", govId);
      formData.append("email", email);
      formData.append("name", name);
      formData.append("phone", contact);

      await api.drivers.update(id, formData);
      setDriver({
        ...driver,
        govId,
        email,
        name,
        phone: contact,
      });
      toast.success("Datos guardados con éxito");
      setError("");
    } catch (error) {
      if (error.message) {
        setFieldErrors(error.message);
      } else {
        setError("Error al actualizar los datos del conductor.");
      }
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (!driver) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [driver]);

  if (!driver) {
    return <Spinner />;
  }

  return (
    <Layout>
      <div
        className="poppins-medium flex flex-col h-screen bg-[#F8F9FB] w-full md:p-8transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-left text-[#1F384C] mb-6">
          Detalles del conductor
        </h1>
        <div className="p-8 border rounded-2xl">
          <ToastContainer />
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <div className="flex flex-col items-center mb-10">
            <div className="w-40 h-40 rounded-full overflow-hidden mb-4">
              <img
                src={driverImg}
                alt={driver.name}
                className="w-full h-full object-cover"
              />
            </div>

            <h2 className="flex flex-col justify-between items-center gap-2 text-xl">
              <div className="h-[38px] px-6 bg-[rgba(255,197,85,0.36)] border border-[#FFC555] text-sm rounded-3xl flex justify-center items-center">
                Conductor
              </div>
              <div className="bg-white p-3 rounded">
                <CustomTextInput
                  label="Nombre del conductor"
                  placeholder="Ingresa el nombre del conductor"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {/* Muestra error específico para el campo "firstName" */}
                {fieldErrors.name && (
                  <p className="text-red-600 text-sm">{fieldErrors.name[0]}</p>
                )}
              </div>
            </h2>
          </div>

          <div className="bg-white p-6 pb-12 rounded-lg shadow-md mb-6">
            <h3 className="text-xl mb-6 text-[#595A5C]">
              Información del conductor
            </h3>

            <div className="flex mx-5 gap-x-32 gap-y-10 flex-wrap">
              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Cédula de identidad"
                    placeholder="Ingresa tu número de documento"
                    type="text"
                    value={govId}
                    onChange={(e) => setGovId(e.target.value)}
                  />
                  {fieldErrors.govId && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.govId[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Correo"
                    placeholder="Ingresa el correo electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.email[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomPhoneInput
                    label="Teléfono"
                    placeholder="Escribe el número de contacto"
                    value={contact}
                    onChange={(phone) => setContact(phone)}
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.phone[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <label className="poppins-bold text-sm text-[#595A5C]">
                  Viajes totales
                </label>
                <span className="inter-regular text-[#595A5C] text-xs">
                  {driver.totalRides}
                </span>
              </div>
            </div>
          </div>

          <div className="md:space-x-4 space-x-3 flex justify-between">
            <button
              onClick={handleSave}
              className="bg-[#59A8D4] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Guardar
            </button>
            <button
              onClick={handleDeleteClick}
              className="bg-[#BE202F] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Eliminar
            </button>
          </div>
        </div>
        <ConfirmDeletePopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          onConfirm={handleDeleteConfirm}
          itemName={driver.name}
        />
      </div>
    </Layout>
  );
}
